/**
 * Moving platforms are a pure function of the simulation tick (DECISIONS.md #20): client and
 * server compute the same pose from the same sidecar entry with nothing to synchronise. A linear
 * path is a triangle wave between two points with an optional rest at each end; a circular path
 * orbits a centre. Only arithmetic that both sides evaluate identically is used.
 */
import { SIM_TICK_RATE } from '../constants.js';
function fract(x) {
    return x - Math.floor(x);
}
/** 0 → 1 → 0 over one period, resting `dwell` of each leg at the ends, eased between. */
export function linearProgress(u, dwell) {
    const tri = u < 0.5 ? u * 2 : 2 - u * 2;
    const span = 1 - 2 * dwell;
    const s = span <= 0 ? (tri < 0.5 ? 0 : 1) : Math.min(1, Math.max(0, (tri - dwell) / span));
    return s * s * (3 - 2 * s);
}
/** The platform's pose at `tick`, written into `out` when given. */
export function platformPoseAt(p, tick, out) {
    const pose = out ?? { center: { x: 0, y: 0, z: 0 }, yaw: p.yaw };
    pose.yaw = p.yaw;
    const seconds = tick / SIM_TICK_RATE;
    const path = p.path;
    const u = fract(seconds / path.periodSeconds + path.phase);
    if (path.kind === 'linear') {
        const s = linearProgress(u, path.dwell);
        pose.center.x = path.from[0] + (path.to[0] - path.from[0]) * s;
        pose.center.y = path.from[1] + (path.to[1] - path.from[1]) * s;
        pose.center.z = path.from[2] + (path.to[2] - path.from[2]) * s;
    }
    else {
        const angle = u * Math.PI * 2 * (path.clockwise ? -1 : 1);
        pose.center.x = path.center[0] + Math.cos(angle) * path.radius;
        pose.center.y = path.center[1];
        pose.center.z = path.center[2] + Math.sin(angle) * path.radius;
    }
    return pose;
}
/** Ticks in one full period of the path (the pose repeats exactly after this many ticks). */
export function platformPeriodTicks(p) {
    return Math.round(p.path.periodSeconds * SIM_TICK_RATE);
}
//# sourceMappingURL=platforms.js.map